import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtN, fmtCents } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';

type Plan = {
  id: string;
  provider: string | null;
  name: string;
  countryCode: string;
  countryName: string;
  dataMb: number;
  durationDays: number;
  priceCents: number;
  currency: string;
  active: boolean;
  createdAt: string;
};
type Env = { rows: Plan[]; page: number; pageSize: number; total: number };

export function EsimPlans() {
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<Env>({
    queryKey: ['/admin/api/esim/plans', page],
    queryFn: () => api.get<Env>(`/admin/api/esim/plans?page=${page}&pageSize=50`),
  });

  const toggle = useMutation({
    mutationFn: (p: Plan) => api.patch(`/admin/api/esim/plans/${p.id}`, { active: !p.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/admin/api/esim/plans'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/api/esim/plans/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/admin/api/esim/plans'] }),
  });

  return (
    <div>
      <PageHeader
        title="eSIM Plans"
        subtitle={data ? `${fmtN(data.total)} plans` : 'Loading…'}
        actions={
          <button onClick={() => setCreating(true)} className="ad-btn-primary">
            <Plus size={14} /> New plan
          </button>
        }
      />

      {creating && <CreatePlanForm onClose={() => setCreating(false)} />}

      <div className="ad-card overflow-hidden">
        <table className="ad-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Country</th>
              <th>Data</th>
              <th>Duration</th>
              <th>Price</th>
              <th>Provider</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center text-text3 py-10">
                  Loading…
                </td>
              </tr>
            ) : (
              data?.rows.map((p) => (
                <tr key={p.id}>
                  <td className="font-medium">{p.name}</td>
                  <td>
                    {p.countryName} <span className="text-text3 text-xs">({p.countryCode})</span>
                  </td>
                  <td>{(p.dataMb / 1024).toFixed(1)} GB</td>
                  <td>{p.durationDays} days</td>
                  <td>{fmtCents(p.priceCents)}</td>
                  <td className="text-text2">{p.provider ?? '—'}</td>
                  <td>
                    <button
                      onClick={() => toggle.mutate(p)}
                      className={`ad-badge ${p.active ? 'ad-badge-success' : 'ad-badge-neutral'} cursor-pointer`}
                    >
                      {p.active ? 'active' : 'inactive'}
                    </button>
                  </td>
                  <td className="text-right">
                    <button
                      onClick={() => {
                        if (confirm(`Delete plan "${p.name}"?`)) del.mutate(p.id);
                      }}
                      className="text-error hover:underline text-xs"
                    >
                      <Trash2 size={12} className="inline" /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
    </div>
  );
}

function CreatePlanForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    countryCode: '',
    countryName: '',
    provider: '',
    dataMb: 1024,
    durationDays: 7,
    priceCents: 999,
    currency: 'USD',
    active: true,
  });

  const create = useMutation({
    mutationFn: () => api.post('/admin/api/esim/plans', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/admin/api/esim/plans'] });
      onClose();
    },
  });

  return (
    <div className="ad-card p-5 mb-4">
      <h3 className="font-semibold mb-3">New plan</h3>
      <div className="grid gap-3 grid-cols-2">
        <Field label="Name">
          <input
            className="ad-input w-full"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Country code (2-letter)">
          <input
            className="ad-input w-full"
            maxLength={2}
            value={form.countryCode}
            onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })}
          />
        </Field>
        <Field label="Country name">
          <input
            className="ad-input w-full"
            value={form.countryName}
            onChange={(e) => setForm({ ...form, countryName: e.target.value })}
          />
        </Field>
        <Field label="Provider">
          <input
            className="ad-input w-full"
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
          />
        </Field>
        <Field label="Data (MB)">
          <input
            type="number"
            className="ad-input w-full"
            value={form.dataMb}
            onChange={(e) => setForm({ ...form, dataMb: Number(e.target.value) })}
          />
        </Field>
        <Field label="Duration (days)">
          <input
            type="number"
            className="ad-input w-full"
            value={form.durationDays}
            onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) })}
          />
        </Field>
        <Field label="Price (cents)">
          <input
            type="number"
            className="ad-input w-full"
            value={form.priceCents}
            onChange={(e) => setForm({ ...form, priceCents: Number(e.target.value) })}
          />
        </Field>
        <Field label="Currency">
          <input
            className="ad-input w-full"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
          />
        </Field>
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={() => create.mutate()} disabled={create.isPending} className="ad-btn-primary">
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <button onClick={onClose} className="ad-btn-secondary">
          Cancel
        </button>
      </div>
      {create.error && <div className="text-error text-sm mt-2">{(create.error as Error).message}</div>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-text3 mb-1">{label}</label>
      {children}
    </div>
  );
}
